import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { HttpError } from '@/lib/https';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import {
  applyCampaignTemplateRest,
  cancelCampaignScheduleRest,
  createCampaignRest,
  deleteCampaignRest,
  duplicateCampaignRest,
  getCampaignAudienceEstimate,
  getCampaignContentRest,
  getCampaignRestById,
  getCampaignsRest,
  getSendJobProgressRest,
  putCampaignContentRest,
  scheduleCampaignRest,
  sendCampaignRest,
  setCampaignAudienceRest,
  testSendCampaignRest,
  updateCampaignRest,
} from '../services/campaigns-rest.service';
import type {
  ApplyTemplateInput,
  AudienceEstimate,
  CancelScheduleResult,
  CampaignContentVersion,
  CampaignRestDetail,
  CampaignRestListResult,
  CreateCampaignRestInput,
  DuplicateCampaignRestInput,
  GetCampaignsRestParams,
  PutCampaignContentInput,
  ScheduleCampaignRestInput,
  SendCampaignRestInput,
  SendJobProgress,
  SendJobResult,
  SetCampaignAudienceInput,
  TestSendInput,
  TestSendResult,
  UpdateCampaignRestInput,
} from '../types/campaigns-rest.types';
import type { CampaignDisplayStatus, CampaignsFilters, CampaignsSortKey } from '../types/campaigns.types';

const invalidateCampaignRest = (
  queryClient: ReturnType<typeof useQueryClient>,
  campaignId?: string
) => {
  queryClient.invalidateQueries({ queryKey: ['campaigns-rest'] });
  if (campaignId) {
    queryClient.invalidateQueries({ queryKey: ['campaigns-rest', 'detail', campaignId] });
  }
  queryClient.invalidateQueries({ queryKey: ['campaigns'] });
  queryClient.invalidateQueries({ queryKey: ['dashboard', 'campaigns'] });
};

const useDebouncedValue = <T>(value: T, delayMs: number): T => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedValue(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);

  return debouncedValue;
};

/** Guide §3 sort values: created | updated | name | scheduled */
const mapSortKeyToRest = (
  sortKey: CampaignsSortKey
): { sort: string; order: 'asc' | 'desc' } => {
  switch (sortKey) {
    case 'name':
      return { sort: 'name', order: 'asc' };
    case 'scheduledAt':
      return { sort: 'scheduled', order: 'desc' };
    case 'createdAt':
      return { sort: 'created', order: 'desc' };
    case 'createdAtAsc':
      return { sort: 'created', order: 'asc' };
    case 'updatedAt':
    default:
      return { sort: 'updated', order: 'desc' };
  }
};

/** Map UI display filters → API status values (guide §3 / prototype STATES). */
export const mapDisplayStatusesToApi = (statuses: CampaignDisplayStatus[]): string[] =>
  statuses.flatMap((status) => {
    switch (status) {
      case 'Draft':
        return ['draft'];
      case 'Scheduled':
        return ['scheduled'];
      case 'Sending':
        return ['sending'];
      case 'Sent':
        return ['sent'];
      case 'Failed':
        return ['failed'];
      case 'Cancelled':
        return ['cancelled'];
      default:
        return [];
    }
  });

export const useGetCampaignRestById = (
  campaignId: string | undefined,
  options: { enabled?: boolean } = {}
) => {
  const id = campaignId?.trim() ?? '';
  // Plain query: pages render their own not-found / error state, so the global "Something went
  // wrong — Not found" toast would only duplicate an expected 404.
  return useQuery<CampaignRestDetail>({
    queryKey: ['campaigns-rest', 'detail', id],
    queryFn: () => getCampaignRestById(id),
    staleTime: 30 * 1000,
    enabled: (options.enabled ?? true) && Boolean(id),
  });
};

export const useGetCampaignsRest = (
  params: GetCampaignsRestParams = {},
  options: { enabled?: boolean } = {}
) =>
  useGlobalQuery<CampaignRestListResult>({
    queryKey: ['campaigns-rest', 'list', params],
    queryFn: () => getCampaignsRest(params),
    staleTime: 30 * 1000,
    enabled: options.enabled ?? true,
  });

export const useSearchCampaignsRest = (
  search: string,
  filters: CampaignsFilters,
  paging: { pageNo?: number; pageSize?: number } = { pageNo: 1, pageSize: 25 },
  sortKey: CampaignsSortKey = 'updatedAt'
) => {
  const debouncedSearch = useDebouncedValue(search, 300);
  const { sort, order } = mapSortKeyToRest(sortKey);
  const status =
    filters.statuses.length > 0 ? mapDisplayStatusesToApi(filters.statuses) : undefined;

  return useGetCampaignsRest({
    pageNo: paging.pageNo ?? 1,
    pageSize: paging.pageSize ?? 25,
    q: debouncedSearch.trim() || undefined,
    status,
    sort,
    order,
    channel: filters.channel && filters.channel !== 'all' ? filters.channel : undefined,
  });
};

export const useGetCampaignAudienceEstimate = (
  campaignId: string | undefined,
  options: { enabled?: boolean } = {}
) => {
  const id = campaignId?.trim() ?? '';
  return useGlobalQuery<AudienceEstimate>({
    queryKey: ['campaigns-rest', 'audience-estimate', id],
    queryFn: () => getCampaignAudienceEstimate(id),
    staleTime: 15 * 1000,
    enabled: (options.enabled ?? true) && Boolean(id),
  });
};

export const useCreateCampaignRest = () => {
  const queryClient = useQueryClient();
  return useGlobalMutation<CampaignRestDetail, Error, CreateCampaignRestInput>({
    mutationFn: createCampaignRest,
    onSuccess: (detail) => invalidateCampaignRest(queryClient, detail.id),
  });
};

export const useUpdateCampaignRest = () => {
  const queryClient = useQueryClient();
  return useGlobalMutation<
    CampaignRestDetail,
    Error,
    { campaignId: string; input: UpdateCampaignRestInput }
  >({
    mutationFn: ({ campaignId, input }) => updateCampaignRest(campaignId, input),
    onSuccess: (_d, vars) => invalidateCampaignRest(queryClient, vars.campaignId),
  });
};

export const useSetCampaignAudienceRest = () => {
  const queryClient = useQueryClient();
  return useGlobalMutation<
    CampaignRestDetail,
    Error,
    { campaignId: string; input: SetCampaignAudienceInput }
  >({
    mutationFn: ({ campaignId, input }) => setCampaignAudienceRest(campaignId, input),
    onSuccess: (_d, vars) => invalidateCampaignRest(queryClient, vars.campaignId),
  });
};

export const useDuplicateCampaignRest = () => {
  const queryClient = useQueryClient();
  return useGlobalMutation<
    CampaignRestDetail,
    Error,
    { campaignId: string; input: DuplicateCampaignRestInput }
  >({
    mutationFn: ({ campaignId, input }) => duplicateCampaignRest(campaignId, input),
    onSuccess: (detail) => invalidateCampaignRest(queryClient, detail.id),
  });
};

export const useDeleteCampaignRest = () => {
  const queryClient = useQueryClient();
  return useGlobalMutation<void, Error, string>({
    mutationFn: deleteCampaignRest,
    onSuccess: (_r, campaignId) => invalidateCampaignRest(queryClient, campaignId),
  });
};

export const useSendCampaignRest = () => {
  const queryClient = useQueryClient();
  return useGlobalMutation<SendJobResult, Error, SendCampaignRestInput>({
    mutationFn: sendCampaignRest,
    onSuccess: (result) => invalidateCampaignRest(queryClient, result.campaignId),
  });
};

export const useScheduleCampaignRest = () => {
  const queryClient = useQueryClient();
  return useGlobalMutation<SendJobResult, Error, ScheduleCampaignRestInput>({
    mutationFn: scheduleCampaignRest,
    onSuccess: (result) => invalidateCampaignRest(queryClient, result.campaignId),
  });
};

export const useCancelCampaignScheduleRest = () => {
  const queryClient = useQueryClient();
  return useGlobalMutation<CancelScheduleResult, Error, string>({
    mutationFn: cancelCampaignScheduleRest,
    onSuccess: (result) => invalidateCampaignRest(queryClient, result.campaignId),
  });
};

const EMPTY_CAMPAIGN_CONTENT: CampaignContentVersion = {
  contentId: '',
  version: 0,
  html: '',
  plainText: null,
  links: [],
  isCurrent: false,
  updatedAt: null,
  updatedBy: null,
  sanitization: { removed: [], count: 0 },
};

export const useGetCampaignContentRest = (
  campaignId: string | undefined,
  options: { enabled?: boolean } = {}
) => {
  const id = campaignId?.trim() ?? '';
  return useGlobalQuery<CampaignContentVersion>({
    queryKey: ['campaigns-rest', 'content', id],
    queryFn: async () => {
      try {
        return await getCampaignContentRest(id);
      } catch (error) {
        // Drafts often have no content yet — treat as empty, not a hard failure.
        if (error instanceof HttpError && error.status === 404) {
          return EMPTY_CAMPAIGN_CONTENT;
        }
        throw error;
      }
    },
    staleTime: 15 * 1000,
    enabled: (options.enabled ?? true) && Boolean(id),
  });
};

/**
 * PUT content and apply-template answer with the new current version (#223) — the same shape GET
 * returns — so it is written straight into the content query: the version label reads N+1 at once
 * instead of the previous number until a refetch lands. The rest is still invalidated, but the
 * content query is not refetched over the value just stored.
 */
const cacheSavedCampaignContent = (
  queryClient: ReturnType<typeof useQueryClient>,
  campaignId: string,
  saved: CampaignContentVersion
) => {
  const contentKey = ['campaigns-rest', 'content', campaignId] as const;
  queryClient.setQueryData(contentKey, saved);
  queryClient.invalidateQueries({
    queryKey: ['campaigns-rest'],
    predicate: (query) =>
      !(query.queryKey[1] === 'content' && query.queryKey[2] === campaignId),
  });
  queryClient.invalidateQueries({ queryKey: ['campaigns'] });
  queryClient.invalidateQueries({ queryKey: ['dashboard', 'campaigns'] });
};

export const usePutCampaignContentRest = () => {
  const queryClient = useQueryClient();
  return useGlobalMutation<
    CampaignContentVersion,
    Error,
    { campaignId: string; input: PutCampaignContentInput }
  >({
    mutationFn: ({ campaignId, input }) => putCampaignContentRest(campaignId, input),
    onSuccess: (saved, vars) => cacheSavedCampaignContent(queryClient, vars.campaignId, saved),
  });
};

export const useApplyCampaignTemplateRest = () => {
  const queryClient = useQueryClient();
  return useGlobalMutation<
    CampaignContentVersion,
    Error,
    { campaignId: string; input: ApplyTemplateInput }
  >({
    mutationFn: ({ campaignId, input }) => applyCampaignTemplateRest(campaignId, input),
    onSuccess: (saved, vars) => cacheSavedCampaignContent(queryClient, vars.campaignId, saved),
  });
};

export const useTestSendCampaignRest = () =>
  useGlobalMutation<TestSendResult, Error, TestSendInput>({
    mutationFn: testSendCampaignRest,
  });

export const useGetSendJobProgressRest = (
  jobId: string | undefined,
  options: { enabled?: boolean; refetchInterval?: number | false } = {}
) => {
  const id = jobId?.trim() ?? '';
  return useGlobalQuery<SendJobProgress>({
    queryKey: ['campaigns-rest', 'send-job-progress', id],
    queryFn: () => getSendJobProgressRest(id),
    staleTime: 0,
    enabled: (options.enabled ?? true) && Boolean(id),
    refetchInterval: options.refetchInterval,
  });
};
